import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormCheckbox,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateDietSimpleDetailsMutation } from '../../hooks'
import { dietPropType } from '../../prop-types'
import { setDietPageIsDirty } from '../../redux/actions/diet-actions'
import { DietAvailableForCategorizationCheckbox } from '../sections'

import { dietSimpleDetailsFormInitialValues } from './initial-values'

const DietSimpleDetailsForm = ({ diet, maxPosition }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DIET.UPDATE_SIMPLE_DETAILS,
  ])

  const [
    updateDietSimpleDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDietSimpleDetailsMutation()

  const initialValues = useMemo(
    () => dietSimpleDetailsFormInitialValues(diet),
    [diet],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateDietSimpleDetails(diet.id, data),
    [diet.id, updateDietSimpleDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Diet details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [
    initialValues.synonyms,
    initialValues.isExcludedFromSearch,
    initialValues.position,
    initialValues.description,
    initialValues.isAvailableForDishCategorization,
    initialValues.isPopular,
    resetForm,
  ])

  useEffect(() => {
    dispatch(setDietPageIsDirty({ isSimpleDetailsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editDiet"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{diet.name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
          <DietAvailableForCategorizationCheckbox
            disabled={!isFormEnabled}
            name="isAvailableForDishCategorization"
            label="Available for Dish Categorization"
          />
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position under category"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="synonyms"
            label="Synonyms (separated by new line)"
            labelAbove
            placeholder="Input synonyms..."
            autoSize
          />
        </Col>
        <Col span={6}>
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Exclude from mobile app search"
            name="isExcludedFromSearch"
          />
        </Col>
        <Col span={6}>
          <FormCheckbox
            disabled={!isFormEnabled}
            label="Is Popular (show in onboarding)"
            name="isPopular"
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.DIET.UPDATE_SIMPLE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

DietSimpleDetailsForm.propTypes = {
  diet: dietPropType.isRequired,
  maxPosition: PropTypes.number.isRequired,
}

export default DietSimpleDetailsForm
